import { A11yModule } from "@angular/cdk/a11y";
import { ChangeDetectionStrategy, Component, inject, OnInit, ViewChild } from "@angular/core";
import { UniversalModalModule } from "@modules/+new/shared/universal-modal/universal-modal.module";
import { CoreModule } from "@modules/core/core.module";
import { CollapsibleContainerUIComponent } from "@modules/shared/collapsible-container/collapsible-container.component";
import {
	IPantosBestAsset,
	WEB3_MERGE_PHASE,
	Web3Facade,
	Web3TrackingEventNames
} from "@modules/web3/+state/web3.facade";
import {
	DYNAMIC_TERMS_TOKEN,
	TermsAndConditionsUpdatesComponent
} from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.component";
import { TermsAndConditionsUpdatesModule } from "@modules/shared/terms-and-conditions/components/terms-and-conditions-updates.module";
import { TermsAndConditionsService } from "@modules/shared/terms-and-conditions/services/terms-and-conditions.service";
import { BehaviorSubject, catchError, combineLatest, delay, EMPTY, Observable, of, switchMap, take, tap } from "rxjs";
import { AgreementBundle, UserJourney } from "@modules/shared/terms-and-conditions/entities/terms-and-conditions.model";
import { Web3RemoteService } from "@modules/web3/services/web3.remote";
import { BpTryAgainErrorModule } from "@v4-components/bp-try-again-error/bp-try-again-error.module";
import { AccountService } from "@modules/account/services/account.service";
import { Web3RemainingAssetListComponent } from "../../ui/remaining-asset/remaining-asset-list.component";
import { Web3DisplayComponent } from "../../ui/web3-display/web3-display.component";
import { InfoService } from "@core-services/info.service";
import { TranslationService } from "@core-services/translation.service";
import { Web3NavigationService } from "@modules/web3/services/web3.navigation";
import { Web3MigrationRoutes } from "@modules/web3/w3st-migration.module";
import { Web3StateService } from "@modules/web3/+state/web3.state.service";
import { Tradable } from "@models/interfaces/tradable";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";
import { IMergeUrlsKeys } from "@modules/web3/entities/merge-urls.interface";

interface IWeb3ViewModel {
	bundles: AgreementBundle[];
	amount: number;
	availableAssets: IPantosBestAsset[];
	hasError: boolean;
	phase: WEB3_MERGE_PHASE;
}

@Component({
	selector: "web3-auto-migration-merge-component",
	imports: [
		CoreModule,
		UniversalModalModule,
		A11yModule,
		CollapsibleContainerUIComponent,
		TermsAndConditionsUpdatesModule,
		BpTryAgainErrorModule,
		Web3RemainingAssetListComponent,
		Web3DisplayComponent
	],
	templateUrl: "./web3-auto-migration-merge.component.component.html",
	styleUrl: "./web3-auto-migration-merge.component.component.scss",
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class Web3AutoMigrationMergeComponentComponent implements OnInit {
	private facade = inject(Web3Facade);
	private termsAndConditionsService = inject(TermsAndConditionsService);
	private remote = inject(Web3RemoteService);
	private accountService = inject(AccountService);
	private infoService = inject(InfoService);
	private translationService = inject(TranslationService);
	private navigation = inject(Web3NavigationService);
	private state = inject(Web3StateService);
	private profileFacade = inject(NewProfileFacade);

	private ready$ = new BehaviorSubject<boolean>(false);
	private userJourney: UserJourney;
	private visionTradable: Tradable;

	isFormValid = false;

	@ViewChild(DYNAMIC_TERMS_TOKEN) dynamic_terms: TermsAndConditionsUpdatesComponent;

	defaultFiat = this.accountService.getDefaultFiat();
	visionTradables$ = this.facade.getVisionTradable$().pipe(tap((tradable) => (this.visionTradable = tradable)));

	whitepaperUrl = `${this.facade.baseURLWithLang}`;
	visionLoyaltyProgrammeUrl = `${this.facade.baseURLWithLang}`;

	vm$: Observable<IWeb3ViewModel> = EMPTY;

	phaseEnum = WEB3_MERGE_PHASE;

	isUKUser = false;

	private init(): void {
		this.vm$ = combineLatest({ mergePhase: this.facade.getMergePhase(), vm: this.facade.getEstimation$() }).pipe(
			switchMap(({ mergePhase, vm }) => {
				this.userJourney = vm.userJourney as UserJourney;
				return combineLatest({
					bundles: this.termsAndConditionsService.getBundlesRemoteErrorUnhandled(this.userJourney),
					amount: of(+vm.amount),
					availableAssets: this.facade.getPantosAndBestBalance$(),
					hasError: of(false),
					phase: of(mergePhase.phase)
				});
			}),
			tap(({ phase, amount }) => {
				this.facade.trackEvent(Web3TrackingEventNames.MigrationEstimateScreenViewed, {
					phase: phase,
					migration_estimate: amount
				});
				this.ready$.next(true);
			}),
			catchError(() => {
				return of({
					hasError: true,
					amount: 0,
					bundles: [] as AgreementBundle[],
					availableAssets: [] as IPantosBestAsset[],
					phase: WEB3_MERGE_PHASE.None
				});
			})
		);
	}

	ngOnInit(): void {
		this.ready$
			.asObservable()
			.pipe(take(2), delay(200))
			.subscribe((enabled) => {
				if (enabled) {
					this.dynamic_terms.termsAndConditionsForm.statusChanges.subscribe((status) => {
						if (status === "VALID") {
							this.isFormValid = true;
						} else {
							this.isFormValid = false;
						}
					});
				}
			});

		this.init();

		//checking profile iso the load URL accordingly
		combineLatest({
			profile: this.profileFacade.getProfile$(),
			mergeUrls: this.facade.getMergeUrls()
		})
			.pipe(take(1))
			.subscribe(({ profile, mergeUrls }) => {
				if (profile.iso === "GB") {
					this.isUKUser = true;
				} else {
					this.isUKUser = false;
				}

				const wpLink = this.facade.getSuffixHelper(IMergeUrlsKeys.whitePaper, mergeUrls);
				const loyaltyLink = this.facade.getSuffixHelper(IMergeUrlsKeys.visionLoyaltyProgramme, mergeUrls);

				this.whitepaperUrl += wpLink;
				this.visionLoyaltyProgrammeUrl += loyaltyLink;
			});
	}

	wiggleToggles(): void {
		this.dynamic_terms.wiggleIt();
	}

	next(phase: WEB3_MERGE_PHASE, migration_amount: number): void {
		this.termsAndConditionsService.acceptTerms(
			[this.userJourney],
			this.dynamic_terms?.termsAndConditionsForm?.value,
			() => {
				//invalidate user signup status and estimation after successful migration
				this.state.invalidateSignedUpSignal();
				this.state.invalidateEstimateSignal();

				if (phase === WEB3_MERGE_PHASE.PostMerge) {
					this.postMergeHandler();
					return;
				}

				this.preMergeHandler(migration_amount);
			}
		);
	}

	private preMergeHandler(amount: number): void {
		this.remote.signUpForWeb3Migration().subscribe({
			next: async (result) => {
				this.facade.trackEvent(Web3TrackingEventNames.MigrationSignUpOptInClicked, {
					phase: this.phaseEnum.PreMerge,
					migration_estimate: amount
				});

				//manually set the signed up signal to true
				this.state.setSignedUpSignal({ signedUp: result?.signedUp });

				await this.navigation.closeModal();
				this.infoService.setSuccess(this.translationService.translate("web3_migration_success_alert"));
			},
			error: () => {
				this.infoService.setError(this.translationService.translate("web3_migration_error_alert"));
			}
		});
	}

	private postMergeHandler(): void {
		this.remote.migrateToWeb3().subscribe({
			next: async ({ value }) => {
				this.facade.trackEvent(Web3TrackingEventNames.MigrationSignUpOptInClicked, {
					phase: WEB3_MERGE_PHASE.PostMerge,
					migration_estimate: +value
				});

				this.state.setSuccessSignal({
					amount: +value,
					tradable: this.visionTradable
				});
				await this.navigation.gotoWeb3ByUrl(Web3MigrationRoutes.autoMigrationSuccess);
			},
			error: () => {
				this.infoService.setError(this.translationService.translate("web3_migration_error_alert"));
			}
		});
	}

	tryAgain(): void {
		this.init();
	}
}
